import { useCallback, useEffect, useRef, useState, useMemo, type ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import fuzzy from 'fuzzy';
import { toast } from 'react-toastify';
import type { Emoji } from '../../../../Components/EmojiPicker';
import { selectProfileName, selectProfileEmail } from 'flowhawk-core';
import { chatRouteSchema } from '../../../../Utils/routeSchema';
import {
  acknowledgeMessage,
  filterPeople,
  markMessagesRead,
  openChatroom,
  setOffset,
  startTyping,
  stopTyping,
} from '../../../../Redux/ChatListActions';
import {
  setMessageText,
  setMessageMedia,
  setMentionedUsers,
  setMentionedNames,
  setFilteredNames,
  submitMessage,
} from '../../../../Redux/ChatMessageActions';
import { setSelectedChatroomMember } from '../../../../Redux/ChatroomActions';
import EmailNotVerified from '../../../../Components/EmailNotVerified';
import MediaLoadingDialog from '../../../../Components/MediaLoadingDialog';
import type { RootState } from '../../../../Redux';
import {
  type AttachmentData,
  type ChatroomInfo,
  type ChatroomMember,
  type ChatroomMemberMap,
  FileMessageTypeStrings,
  type MessageId,
  type MimeTypeString,
  type ReadStatusMap,
  acceptableFileTypes,
} from '../../../../Utils/types';
import { useSocketStatus } from '../../../../Selectors/hooks';
import { useFeaturePermissions } from '../../../../Selectors/Permission';
import { isEmojiType } from '../../../../Utils/emojiRegex';
import { resetCaseNotificationState } from '../../../../Redux/CaseNotificationActions';
import { resetDonorSelection } from '../../../../Redux/DonorActions';
import { CaseNotificationWizardStep } from '../../../../Utils/types';
import ChatHistory from '../../../../Components/ChatHistory';
import SagaMessage from '../../../../Components/SagaMessage';
import ChatInput from '../../../../Components/ChatInput';
import PinnedMessagesPanel from '../../../../Components/PinnedMessagesPanel';
// import PinnedMessagesPanel from '../../../../Components/PinnedMessagesPanel';
import ApplicationStyles from '../../../../Themes/ApplicationStyles';
import ChatMessageStyles from '../../../../Components/Styles/ChatMessageStyles';
import { headerHeight } from '../../../../Components/Styles/HeaderStyles';
import Images from '../../../../Themes/Images';
import { classNames } from '../../../../Utils/classnames';

export const Route = createFileRoute('/(main)/_layout/chats/_chats/$chatId')({
  params: {
    parse: (params) => chatRouteSchema.parse(params),
  },
  component: RouteComponent,
});

const emptyChatMessageMedia: AttachmentData[] = [];
const emptyMessageOrder: MessageId[] = [];
const emptyMentionedNames: string[] = [];
const emptyMentionedUsers: number[] = [];
const emptyFilteredNames: string[] = [];
const emptyMemberNames: string[] = [];
const emptyReadStatus: ReadStatusMap = {};

const fileDropErrorToast: number = 10003;
const noUploadToast: number = 10006;
const genericSagaToast: number = 10007;

const noop = () => {};

// How long a dispatched mark-read is assumed to still be in progress. Must exceed the worst case in
//  markMessagesReadSaga (a 1s debounce plus the 30s socket ack timeout) so that a slow-but-successful
//  ack isn't re-sent
const MARK_READ_IN_FLIGHT_MS = 35000;

function RouteComponent() {
  const { chatId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  // Refs
  const dropRef = useRef<HTMLDivElement>(null);
  const fileUploadRef = useRef<HTMLInputElement>(null);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const typingTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const markReadInFlightRef = useRef<{ untilId: MessageId; at: number } | null>(null);
  // Holds the newest lastVisibleId that arrived while a mark-read was in flight and got suppressed,
  //  so that progress the user made during that window isn't silently dropped. Flushed either as soon
  //  as the in-flight guard clears (ack observed) or by pendingReadTimeoutRef as a fallback.
  const pendingReadIdRef = useRef<MessageId | null>(null);
  const pendingReadTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  // Use refs for onMessageVisible state so it survives re-renders: as local variables
  //  inside the handler they were discarded whenever the handler was rebuilt, which both reset the
  //  batch mid-collection and orphaned the pending timeout so it fired against a stale room.
  const visibilityTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const latestVisibleIdRef = useRef<MessageId | null>(null);

  // Local state
  const [dragging, setDragging] = useState(false);
  const [isMessageDetailsOpen, setIsMessageDetailsOpen] = useState(false);
  const [tempLastVisibleMessage, setTempLastVisibleMessage] = useState<MessageId | null>(null);
  const [showMention, setShowMention] = useState(false);
  const [showBookmarksPanel, setShowBookmarksPanel] = useState(false);
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);

  // Redux selectors
  const { isConnected } = useSocketStatus();
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const profile = useSelector((state: RootState) => state.auth.profile);
  const chatroomState = useSelector((state: RootState) => state.chatroom);
  const editData = useSelector((state: RootState) => state.chatEdit);
  const memberId = profile?.userId ?? -1;
  const chatMessageText = useSelector((state: RootState) => state.chatMessage.messages[chatId]?.text || '');
  const chatMessageMedia = useSelector(
    (state: RootState) => state.chatMessage.messages[chatId]?.attachments || emptyChatMessageMedia,
  );
  const selectedResult = useSelector((state: RootState) => state.chatList.selectedResult);
  const room: ChatroomInfo | undefined = useSelector((state: RootState) => state.chatList.chats[chatId]);
  const msgOrder = room?.messageOrder ?? emptyMessageOrder;
  const totalMessages = (room ? room.totalMessages : msgOrder.length) || 0;
  const loadMoreMessages = totalMessages > msgOrder.length;
  const messageOrder = useMemo(() => msgOrder.slice(0, msgOrder.length), [msgOrder]);
  const messages = useMemo(
    () => messageOrder.map((msgKey) => room?.messages[`${msgKey}`]).filter((msg) => Boolean(msg)),
    [messageOrder, room],
  );
  const mentionedNames = useSelector(
    (state: RootState) => state.chatMessage.messages[chatId]?.mentionedNames || emptyMentionedNames,
  );
  const mentionedUsers = useSelector(
    (state: RootState) => state.chatMessage.messages[chatId]?.mentionedUsers || emptyMentionedUsers,
  );
  const filteredNames = useSelector(
    (state: RootState) => state.chatMessage.messages[chatId]?.filteredNames || emptyFilteredNames,
  );
  const isTyping = useSelector((state: RootState) => state.chatList.isTyping);
  const isLoadingMessages = useSelector((state: RootState) => state.loading.chatMessages[chatId] || false);
  const isLoadingRooms = useSelector((state: RootState) => state.loading.roomsList);
  const mediaLoading = useSelector((state: RootState) => state.loading.media);
  const peopleFilter = useSelector((state: RootState) => state.chatList.peopleFilter);
  const deepSearchFilter = useSelector((state: RootState) => state.chatList.deepSearchFilter);
  const featurePermissions = useFeaturePermissions();

  // Derived values
  const maxWidth = Math.floor(0.68 * windowWidth);
  const fileDropStyle = dragging
    ? {
        ...ChatMessageStyles.chatFileDropWrapper,
        ...{
          width: window.innerWidth - 20,
          height: window.innerHeight - headerHeight - 20,
        },
      }
    : {};
  const readStatus = room?.readStatus ?? emptyReadStatus;
  const members = room?.members ?? {};
  const memberIds = room?.memberOrder ?? [];
  const membersTyping = room?.membersTyping ?? [];
  const memberNames = room?.memberNames ?? emptyMemberNames;
  const namesForMention = filteredNames.length === 0 ? memberNames : filteredNames;

  // Open chat room when id changes
  useEffect(() => {
    dispatch(openChatroom(chatId));

    return () => {
      // Abandon any debounced visibility batch belonging to the room we are leaving,
      // so it doesn't fire after the switch
      clearTimeout(visibilityTimeoutRef.current);
      clearTimeout(pendingReadTimeoutRef.current);
      pendingReadTimeoutRef.current = undefined;
      latestVisibleIdRef.current = null;
      pendingReadIdRef.current = null;
      markReadInFlightRef.current = null;
    };
  }, [dispatch, chatId]);

  // Keep windowWidth in sync with the window size so that message widths don't stay frozen at the size present on the last render.
  useEffect(() => {
    let raf = 0;
    const handleResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setWindowWidth((prev) => (prev !== window.innerWidth ? window.innerWidth : prev));
      });
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Flush pending tempLastVisibleMessage when rooms finish loading
  useEffect(() => {
    if (!isLoadingRooms && tempLastVisibleMessage) {
      if (room?.messageOrder) {
        dispatch(markMessagesRead(room.id, memberId, tempLastVisibleMessage));
      }
      setTempLastVisibleMessage(null);
    }
  }, [isLoadingRooms, tempLastVisibleMessage, room, memberId, dispatch]);

  // Clean up typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const handleFiles = useCallback(
    (files: File[]) => {
      const mediaArray = [...chatMessageMedia];
      files.forEach((file) => {
        mediaArray.push({
          file,
          fileName: file.name,
          path: URL.createObjectURL(file),
          mime: file.type as MimeTypeString,
          size: file.size,
          displayName: file.name,
        });
      });

      // Remove duplicate objects by filename
      const result = mediaArray.reduce<AttachmentData[]>((unique, o) => {
        if (!unique.some((obj) => obj.fileName === o.fileName)) {
          unique.push(o);
        }
        return unique;
      }, []);

      if (result.length < mediaArray.length) {
        toast(
          <div style={{ ...ApplicationStyles.toastContainer }}>
            <img height="40" width="30" src={Images.xvivoSymbol} alt="XVIVO Logo" style={{ marginRight: 20 }} />
            <p style={{ ...ApplicationStyles.toastText }}>A file with the same name is already being uploaded.</p>
          </div>,
          { toastId: noUploadToast },
        );
      }

      dispatch(setMessageMedia(chatId, result));
      chatInputRef.current?.focus();
      setDragging(false);
      dragCounterRef.current = 0;
    },
    [dispatch, chatId, chatMessageMedia],
  );

  const processMediaFiles = useCallback(
    (files: FileList) => {
      const filesToUpload: File[] = [];
      if (files.length > 0) {
        for (let i = 0; i < files.length; i += 1) {
          const { type } = files[i];
          if (FileMessageTypeStrings.includes(type)) {
            filesToUpload.push(files[i]);
          } else {
            if (!toast.isActive(fileDropErrorToast)) {
              toast(
                <div style={{ ...ApplicationStyles.toastContainer }}>
                  <img height="40" width="30" src={Images.xvivoSymbol} alt="XVIVO Logo" style={{ marginRight: 20 }} />
                  <p style={{ ...ApplicationStyles.toastText }}>
                    Invalid file type. Accepted files include .png, .jpeg, .jpg, .mp4, .mov, .pdf, .xml, .csv, .doc, and
                    .xls.
                  </p>
                </div>,
                { toastId: fileDropErrorToast },
              );
            }
            setDragging(false);
          }
        }
        handleFiles(filesToUpload);
      } else {
        if (!toast.isActive(fileDropErrorToast)) {
          toast(
            <div style={{ ...ApplicationStyles.toastContainer }}>
              <img height="40" width="30" src={Images.xvivoSymbol} alt="XVIVO Logo" style={{ marginRight: 20 }} />
              <p style={{ ...ApplicationStyles.toastText }}>Before uploading a file, please select a chatroom.</p>
            </div>,
            { toastId: fileDropErrorToast },
          );
        }
        setDragging(false);
      }
    },
    [handleFiles],
  );

  // ESC key handler to close drag overlay
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dragging) {
        setDragging(false);
        dragCounterRef.current = 0;
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [dragging]);

  // Drag-and-drop event handlers
  useEffect(() => {
    const dropDiv = dropRef.current;
    if (!dropDiv) return;

    const handleDrag = (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const handleDragIn = (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      dragCounterRef.current += 1;
      setDragging(true);
    };

    const handleDragOut = (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      dragCounterRef.current -= 1;
      if (dragCounterRef.current === 0) {
        setDragging(false);
      }
    };

    const handleDrop = (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setDragging(false);
      dragCounterRef.current = 0;
      if (event?.dataTransfer?.files && event.dataTransfer.files.length > 0) {
        processMediaFiles(event.dataTransfer.files);
      }
      event?.dataTransfer?.clearData();
    };

    dropDiv.addEventListener('dragenter', handleDragIn);
    dropDiv.addEventListener('dragleave', handleDragOut);
    dropDiv.addEventListener('dragover', handleDrag);
    dropDiv.addEventListener('drop', handleDrop);

    return () => {
      dropDiv.removeEventListener('dragenter', handleDragIn);
      dropDiv.removeEventListener('dragleave', handleDragOut);
      dropDiv.removeEventListener('dragover', handleDrag);
      dropDiv.removeEventListener('drop', handleDrop);
    };
  }, [processMediaFiles]);

  // --- Core Callbacks ---
  const onLoadMoreMessages = useCallback(() => {
    if (room?.id) {
      // Set the new offset to the number of messages we currently have
      const newOffset = room.messageOrder ? room.messageOrder.length : 0;
      dispatch(setOffset(room.id, memberId, newOffset));
    }
  }, [dispatch, room, memberId]);

  const commitMessagesRead = useCallback(
    (lastVisibleId: MessageId) => {
      if (room?.messageOrder && lastVisibleId) {
        // Suppress duplicate dispatches while a mark is outstanding. The observer keeps reporting the
        //  same messages as visible until the ack round-trips and flips seenByCurrentUser, so without
        //  this we would re-send the same read on every batch.
        //  This tracks what is in flight. If a mark never lands (a dropped ack, an untilId missing from messageOrder)
        //  it expires and is retried on the next batch, instead of wedging the room's badge until the
        //  user navigates away. Marking read is idempotent — the saga re-filters on seenByCurrentUser
        //  and sends nothing when there is nothing left to mark.
        const inFlight = markReadInFlightRef.current;

        if (inFlight && Date.now() - inFlight.at < MARK_READ_IN_FLIGHT_MS) {
          // Track newer message ids seen while the previous mark-read is still in flight
          //  and flush them once the guard clears (ack, or this fallback timeout if the ack
          //  never arrives), so messages the user scrolled past mid-window don't stay unread forever.
          const currentPending = pendingReadIdRef.current;

          if (
            currentPending === null ||
            room.messageOrder.indexOf(lastVisibleId) > room.messageOrder.indexOf(currentPending)
          ) {
            pendingReadIdRef.current = lastVisibleId;
          }

          if (!pendingReadTimeoutRef.current) {
            const remaining = Math.max(MARK_READ_IN_FLIGHT_MS - (Date.now() - inFlight.at), 0);

            pendingReadTimeoutRef.current = setTimeout(() => {
              pendingReadTimeoutRef.current = undefined;
              const pendingId = pendingReadIdRef.current;
              pendingReadIdRef.current = null;
              if (pendingId) commitMessagesRead(pendingId);
            }, remaining);
          }
        } else {
          // This call is proceeding, so any earlier suppressed progress is superseded — drop it and
          //  cancel its fallback flush so we don't double-dispatch later.
          pendingReadIdRef.current = null;
          clearTimeout(pendingReadTimeoutRef.current);
          pendingReadTimeoutRef.current = undefined;

          markReadInFlightRef.current = { untilId: lastVisibleId, at: Date.now() };
          if (isLoadingRooms) {
            setTempLastVisibleMessage(lastVisibleId);
          } else {
            dispatch(markMessagesRead(room.id, memberId, lastVisibleId));
          }
        }
      }
    },
    [dispatch, room, memberId, isLoadingRooms],
  );

  // Clear the in-flight mark-read guard once the store confirms the message was marked, so the next
  //  batch of visible messages can be committed immediately. If a suppressed batch left a further-along
  //  id pending, flush it now instead of leaving it stranded until an unrelated visibility change.
  useEffect(() => {
    const inFlight = markReadInFlightRef.current;

    if (inFlight && room?.messages[`${inFlight.untilId}`]?.seenByCurrentUser) {
      markReadInFlightRef.current = null;

      const pendingId = pendingReadIdRef.current;

      if (pendingId) {
        clearTimeout(pendingReadTimeoutRef.current);
        pendingReadTimeoutRef.current = undefined;
        pendingReadIdRef.current = null;
        commitMessagesRead(pendingId);
      }
    }
  }, [room, commitMessagesRead]);

  // keep onMessageVisible state stable AND current by using refs
  const visibilityInputsRef = useRef({
    dragging,
    isMessageDetailsOpen,
    room,
    messageOrder,
    memberId,
    commitMessagesRead,
  });

  useEffect(() => {
    visibilityInputsRef.current = { dragging, isMessageDetailsOpen, room, messageOrder, memberId, commitMessagesRead };
  }, [dragging, isMessageDetailsOpen, room, messageOrder, memberId, commitMessagesRead]);

  const onMessageVisible = useCallback((messageId: MessageId) => {
    if (visibilityInputsRef.current.dragging || visibilityInputsRef.current.isMessageDetailsOpen) {
      return;
    }

    // Only capture the latest messageId
    const currentOrder = visibilityInputsRef.current.messageOrder;
    const latestSoFar = latestVisibleIdRef.current;

    if (latestSoFar === null || currentOrder.indexOf(messageId) > currentOrder.indexOf(latestSoFar)) {
      latestVisibleIdRef.current = messageId;
    }

    clearTimeout(visibilityTimeoutRef.current);

    visibilityTimeoutRef.current = setTimeout(() => {
      const { room: currentRoom, messageOrder: order, memberId: currentMemberId } = visibilityInputsRef.current;
      const latestMessageId = latestVisibleIdRef.current;

      if (currentRoom && order && latestMessageId) {
        // Simplified. Previously tracked lastVisible in state, compared here and only updated if it had changed.
        //             That left a gap where new messages have been received this session before the chatroom was opened (not uncommon)
        //             componentDidUpdate would fire and trigger this method before the messages were retrieved into state
        //             In turn, the newest message was marked as both read and the last visible so nothing earlier was checked when
        //             the messages did finally load. The timing of async events made this unpredictable
        //             This simpler version always checks for unread messages at or before the current last read message
        //             This might mean traversing this array more often than scrictly necessary but that is probably a sub-ms operation
        //             The simplicity also means future errors will be easier to identify.
        const indexOfLast = order.indexOf(latestMessageId);

        for (let i = indexOfLast; i >= 0; i -= 1) {
          const currentMessageId = order[i];
          const message = currentRoom.messages[String(currentMessageId)];

          if (message && !message.seenByCurrentUser && message.senderId !== currentMemberId) {
            visibilityInputsRef.current.commitMessagesRead(currentMessageId);
            break;
          }
        }
      }

      // Clear latestMessageId for next batch
      latestVisibleIdRef.current = null;
    }, 300);
  }, []);

  const onOpenMessageDetails = useCallback(() => setIsMessageDetailsOpen(true), []);
  const onCloseMessageDetails = useCallback(() => setIsMessageDetailsOpen(false), []);

  const sendAck = useCallback(
    (emoji: string, messageId: MessageId) => {
      const msgId = typeof messageId === 'number' ? messageId : parseInt(messageId, 10);
      let comp: number;
      if (emoji.length === 1) {
        comp = emoji.charCodeAt(0);
      } else {
        comp = (emoji.charCodeAt(0) - 0xd800) * 0x400 + (emoji.charCodeAt(1) - 0xdc00) + 0x10000;
        if (comp < 0) {
          comp = emoji.charCodeAt(0);
        }
      }
      dispatch(acknowledgeMessage(chatId, msgId, `U+${comp.toString(16).toUpperCase()}`));
    },
    [dispatch, chatId],
  );

  const onSearchPeople = useCallback(
    (filter: string) => {
      dispatch(filterPeople(filter));
    },
    [dispatch],
  );

  const onSend = useCallback(
    (highPriority: boolean = false) => {
      dispatch(submitMessage(chatId, highPriority));
      chatInputRef.current?.focus();
      setShowMention(false);

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = undefined;
      }
      if (isTyping) {
        dispatch(stopTyping(chatId));
      }
      dispatch(setMentionedNames(chatId, []));
    },
    [dispatch, chatId, isTyping],
  );

  const onSetMessage = useCallback(
    (text: ChangeEvent<HTMLTextAreaElement> | Emoji) => {
      // Typing indicator management
      if (!isTyping) {
        dispatch(startTyping(chatId));
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = undefined;
      }
      typingTimeoutRef.current = setTimeout(() => {
        dispatch(stopTyping(chatId));
      }, 3000);

      if (isEmojiType(text)) {
        if (text.native) {
          setShowMention(false);
          const textAndEmoji = chatMessageText ? chatMessageText + text.native : text.native;
          dispatch(setMessageText(chatId, textAndEmoji));
          chatInputRef.current?.focus();
        }
      } else {
        if (text.target.value === '') {
          dispatch(setMentionedUsers(chatId, []));
          dispatch(setMentionedNames(chatId, []));
          dispatch(setFilteredNames(chatId, memberNames));
          setShowMention(false);
        } else {
          if (/@(\S+)$/.test(text.target.value) || /[@]$/g.test(text.target.value)) {
            setShowMention(true);
            const typedName = text.target.value.match(/@(\S+)$/);
            if (typedName) {
              const results = fuzzy.filter(typedName[1], memberNames).map((name) => name.string);
              dispatch(setFilteredNames(chatId, results));
              if (results.length === 0) {
                setShowMention(false);
              }
            } else {
              dispatch(setFilteredNames(chatId, memberNames));
            }
          } else {
            dispatch(setFilteredNames(chatId, memberNames));
            setShowMention(false);
          }

          // Remove mentions that were deleted from text
          const currentMentionedNames = [...mentionedNames];
          const currentMentionedUsers = [...mentionedUsers];
          for (let i = currentMentionedNames.length - 1; i >= 0; i -= 1) {
            if (!text.target.value.includes(currentMentionedNames[i])) {
              currentMentionedNames.splice(i, 1);
              currentMentionedUsers.splice(i, 1);
            }
          }
          dispatch(setMentionedNames(chatId, currentMentionedNames));
          dispatch(setMentionedUsers(chatId, currentMentionedUsers));
        }

        dispatch(setMessageText(chatId, text.target.value));
      }
    },
    [dispatch, chatId, chatMessageText, memberNames, mentionedNames, mentionedUsers, isTyping],
  );

  const onSetMediaMessage = useCallback(
    (attachments: AttachmentData[]) => {
      dispatch(setMessageMedia(chatId, attachments));
    },
    [dispatch, chatId],
  );

  const handleSubmit = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (fileUploadRef.current?.files && fileUploadRef.current.files.length > 0) {
        const { files } = fileUploadRef.current;
        const filesToUpload = Array.from(files).map((file) => file);
        handleFiles(filesToUpload);
      } else {
        if (!toast.isActive(fileDropErrorToast)) {
          toast(
            <div style={{ ...ApplicationStyles.toastContainer }}>
              <img height="40" width="30" src={Images.xvivoSymbol} alt="XVIVO Logo" style={{ marginRight: 20 }} />
              <p style={{ ...ApplicationStyles.toastText }}>Before uploading an image, please select a room.</p>
            </div>,
            { toastId: fileDropErrorToast },
          );
        }
        setDragging(false);
      }
    },
    [handleFiles],
  );

  const onOpenUploadMedia = useCallback(() => {
    setDragging(true);
  }, []);

  const closeMention = useCallback(() => {
    setShowMention(false);
  }, []);

  const handleToggleBookmarksPanel = useCallback(() => {
    setShowBookmarksPanel((prev) => !prev);
  }, []);

  const handleCloseBookmarksPanel = useCallback(() => {
    setShowBookmarksPanel(false);
  }, []);

  const handleJumpToMessage = useCallback(
    (messageId: MessageId) => {
      const message = document.getElementById(`message${messageId}`);
      if (message) {
        message.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        handleCloseBookmarksPanel();
      } else {
        // Message not loaded, need to load more messages
        onLoadMoreMessages();
      }
    },
    [handleCloseBookmarksPanel, onLoadMoreMessages],
  );

  const handleShowMemberProfile = useCallback(
    (selectedMember: ChatroomMember) => {
      dispatch(setSelectedChatroomMember(selectedMember));
      navigate({
        to: '.',
        search: (prev) => ({
          ...prev,
          sidebar: 'member-profile',
        }),
      });
    },
    [dispatch, navigate],
  );

  const addMentionedUser = useCallback(
    (addedMemberId: number, memberMap: ChatroomMemberMap) => {
      const mentioned = selectProfileName(
        memberMap[addedMemberId.toString()].profile,
        selectProfileEmail(memberMap[addedMemberId.toString()].profile),
      );

      let newMessage = '';
      const result = chatMessageText.match(/@(\S+)$/);

      if (result && result.index !== undefined) {
        const { length } = result[1];
        const index = result.index + 1;
        const beforeMention = chatMessageText.slice(0, index);
        const afterMention = chatMessageText.slice(index + length);
        newMessage = `${beforeMention + mentioned} ${afterMention}`;
      } else {
        newMessage = `${chatMessageText + mentioned} `;
      }

      const newMentionIds = [...mentionedUsers];
      const newMentionNames = [...mentionedNames];

      if (!newMentionIds.includes(addedMemberId) && !newMentionNames.includes(`@${mentioned}`)) {
        newMentionIds.push(addedMemberId);
        newMentionNames.push(`@${mentioned}`);
      }

      dispatch(setMessageText(chatId, newMessage));
      dispatch(setMentionedUsers(chatId, newMentionIds));
      dispatch(setMentionedNames(chatId, newMentionNames));
      dispatch(setFilteredNames(chatId, memberNames));

      setShowMention(false);
      chatInputRef.current?.focus();
    },
    [dispatch, chatId, chatMessageText, mentionedUsers, mentionedNames, memberNames],
  );

  const location = useLocation();

  const openNotificationWizard = useCallback(() => {
    if (!room?.donorId) return;

    const prevSidebarLocation = { pathname: location.pathname, search };
    dispatch(resetDonorSelection([room.donorId]));
    dispatch(resetCaseNotificationState({ step: CaseNotificationWizardStep.SelectFields, prevSidebarLocation }));
    navigate({ to: '.', search: (prev) => ({ ...prev, sidebar: 'notify', 'sidebar-closed': undefined }) });
  }, [dispatch, navigate, location.pathname, search, room?.donorId]);

  return (
    <div ref={dropRef} className={classNames('chat-main-inner', { 'file-dragging': dragging })}>
      <div style={fileDropStyle}>
        {dragging && (
          <form style={ChatMessageStyles.chatFileDropForm}>
            <img style={ChatMessageStyles.uploadIcon} src={Images.fileUpload} alt="File Upload" />
            <label htmlFor="file" style={ChatMessageStyles.fileDropFormButton}>
              Choose file from computer
              <input
                type="file"
                name="message"
                id="file"
                accept={acceptableFileTypes}
                multiple
                ref={fileUploadRef}
                onChange={handleSubmit}
                style={{ display: 'none' }}
              />
            </label>
            or drag and drop to upload.
            <span style={ChatMessageStyles.fileEscapeMessage}>Press ESC to exit.</span>
          </form>
        )}
      </div>
      <MediaLoadingDialog mediaLoading={mediaLoading} />
      <EmailNotVerified />
      <SagaMessage toastId={genericSagaToast} />
      <ChatHistory
        chatId={chatId}
        chatInputRef={chatInputRef}
        maxWidth={maxWidth}
        accessToken={accessToken}
        memberId={memberId}
        selectedResult={selectedResult}
        messages={messages}
        isLoadingMessages={isLoadingMessages}
        totalMessages={totalMessages}
        members={members}
        memberIds={memberIds}
        selected={true}
        loadMoreMessages={loadMoreMessages}
        readStatus={readStatus}
        user={profile}
        editData={editData}
        chatroomState={chatroomState}
        deepSearchFilter={deepSearchFilter}
        peopleFilter={peopleFilter}
        paginate={onLoadMoreMessages}
        onMessageVisible={onMessageVisible}
        openMessageDetails={onOpenMessageDetails}
        closeMessageDetails={onCloseMessageDetails}
        sendAck={sendAck}
        filterPeople={onSearchPeople}
        onCreateChatroom={noop}
        setName={noop}
        setDescription={noop}
        addUsersToChatroom={noop}
        onCancelButtonClick={noop}
        selectV2Option={noop}
        showMemberProfile={handleShowMemberProfile}
        onShowDonor={noop}
        showPinnedPanel={showBookmarksPanel}
        onTogglePinnedPanel={handleToggleBookmarksPanel}
        room={room}
        sidebarType={search.sidebar}
      />
      <PinnedMessagesPanel
        isOpen={showBookmarksPanel}
        pinnedMessageIds={room?.pinnedOrder ?? []}
        pinnedMessages={room?.pinnedMessages ?? {}}
        messages={room?.messages ?? {}}
        members={members}
        isLoadingMessages={isLoadingMessages}
        onClose={handleCloseBookmarksPanel}
        onJumpToMessage={handleJumpToMessage}
        onLoadMoreMessages={onLoadMoreMessages}
        isRightSidebarOpen={Boolean(search.sidebar)}
      />
      <ChatInput
        inputRef={chatInputRef}
        showMention={showMention}
        members={members}
        memberIds={memberIds}
        membersTyping={membersTyping}
        filteredNames={namesForMention}
        message={chatMessageText}
        media={chatMessageMedia}
        mentionedNames={mentionedNames}
        chatroomName={''}
        createChatroom={false}
        selected={true}
        isConnected={isConnected}
        canUseNotificationWizard={featurePermissions.notificationWizard}
        setMessage={onSetMessage}
        onSend={onSend}
        setMessageMedia={onSetMediaMessage}
        onUploadMedia={onOpenUploadMedia}
        onPasteMedia={processMediaFiles}
        closeMention={closeMention}
        addMentionedUser={addMentionedUser}
        onShowCaseNotificationWizard={room?.donorId ? openNotificationWizard : undefined}
      />
    </div>
  );
}
